"use client";

import Image from "next/image";

import { TESTIMONIALS } from "@/constants/home.constants";
import { useTestimonialCarousel } from "@/hooks/useTestimonialCarousel";

export default function TestimonialsSection() {
  const { active, setActive, prev, next } = useTestimonialCarousel();
  const item = TESTIMONIALS[active];

  return (
    <section className="reveal-section space-y-8 py-4">
      <div className="text-center space-y-3">
        <p className="text-xs uppercase tracking-[0.25em] text-[var(--accent)]">What Clients Say</p>
        <h2 className="text-4xl md:text-5xl">Loved by founders and CTOs alike.</h2>
      </div>

      <div className="relative flex items-center gap-4">
        {/* Prev arrow */}
        <button
          onClick={prev}
          className="hidden md:flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[var(--stroke)] text-lg transition hover:border-[var(--accent)]"
          aria-label="Previous review"
        >
          ‹
        </button>

        {/* Card */}
        <div className="flex-1 rounded-2xl border border-[var(--stroke)] bg-[var(--panel)]/60 px-8 py-10 text-center">
          {/* Quote icon */}
          <div className="mb-6 text-4xl text-[var(--accent)] leading-none">&ldquo;&rdquo;</div>

          <blockquote className="text-lg leading-relaxed text-[var(--text)] transition-all duration-500">
            &ldquo;{item.quote}&rdquo;
          </blockquote>

          {/* Stars */}
          <div className="mt-5 flex justify-center gap-1">
            {Array.from({ length: 5 }).map((_, i) => (
              <span key={i} className="text-[var(--accent)]">★</span>
            ))}
          </div>

          {/* Author. If the testimonial provides a portrait photo we
              render it as the avatar; the initials chip is the fallback. */}
          <div className="mt-5 flex items-center justify-center gap-3">
            {item.photo ? (
              <div className="relative h-10 w-10 shrink-0 overflow-hidden rounded-full border border-[var(--stroke)] bg-slate-800">
                <Image
                  src={item.photo}
                  alt={item.author}
                  fill
                  sizes="40px"
                  className="object-cover"
                />
              </div>
            ) : (
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[var(--accent)]/20 text-sm font-semibold text-[var(--accent)]">
                {item.initials}
              </div>
            )}
            <div className="text-left">
              <p className="text-sm font-semibold">{item.author}</p>
              <p className="text-xs text-[var(--subtle)]">{item.role}</p>
            </div>
          </div>
        </div>

        {/* Next arrow */}
        <button
          onClick={next}
          className="hidden md:flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[var(--stroke)] text-lg transition hover:border-[var(--accent)]"
          aria-label="Next review"
        >
          ›
        </button>
      </div>

      {/* Dots */}
      <div className="flex justify-center gap-2">
        {TESTIMONIALS.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => setActive(i)}
            aria-label={`Show review ${i + 1}`}
            aria-current={active === i}
            className="group relative inline-flex h-11 w-11 items-center justify-center"
          >
            <span
              className={`block h-1.5 rounded-full transition-all duration-300 ${
                active === i ? "w-7 bg-[var(--accent)]" : "w-1.5 bg-[var(--stroke)] group-hover:bg-[var(--muted)]"
              }`}
            />
          </button>
        ))}
      </div>
    </section>
  );
}
